import { getSlideSize } from "../source-notes/slideInfo";
import { logger } from "../tslog";
import { SlideShapeInfo } from "../../types/contentFraming";
import { 
  detectSlideTitle, 
  detectSlideSubtitle, 
  detectSourceAndNotes, 
  detectPlaceholderTitleAndSubtitle, 
  detectKeyTakeAways,
} from "./shapeDetection";

// Function to process text with different line break formats and convert to numbered list
function processTextWithNumbering(text: string): string {
  if (!text || text.trim() === "") {
    return "";
  }

  // Handle different line break formats
  let lines: string[] = [];
  
  // Check for \r\n (Windows line breaks)
  if (text.includes('\r\n')) {
    lines = text.split('\r\n');
  }else if (text.includes('\n')) {
  // Check for \n (Unix line breaks)
    lines = text.split('\n');
  }else if (text.includes('\r')) {
  // Check for \r (Mac line breaks)
    lines = text.split('\r');
  }else {
    // If no line breaks, treat as single line
    lines = [text];
  }

  // Filter out empty lines and trim whitespace
  const nonEmptyLines = lines
    .map(line => line.trim())
    .filter(line => line.length > 0);

  // If only one line or no lines, return as is
  if (nonEmptyLines.length <= 1) {
    return nonEmptyLines[0] || "";
  }

  // Convert to numbered list, skipping lines that start with "notes"
  let numberCounter = 1;
  return nonEmptyLines
    .map((line) => {
      const lowerLine = line.toLowerCase();
      if (lowerLine.startsWith('notes')) {
        return line; // Don't add number to lines starting with "notes"
      }else {
        return `${numberCounter++}. ${line}`;
      }
    })
    .join('\n');
}


// Returns all shapes on the specified slide along with slide height
export async function getAllShapesFromPlaceholderSlide(slideId: string, targetShapeId: string): Promise<SlideShapeInfo[]> {
  if (!slideId || typeof slideId !== "string") {
    throw new Error("getAllShapesFromPlaceholderSlide: slideId is required");
  }

  return PowerPoint.run(async (context) => {
    
    const slide = context.presentation.slides.getItem(slideId.trim());
    
    slide.load(["id"]);
    await context.sync();

    const { width: slideWidth, height: slideHeight } = await getSlideSize(context, slide);
    
    logger.info("slide size", {
      slideWidth: slideWidth,
      slideHeight: slideHeight,
    });
    // Load slide dimensions and id
    

    // Load shapes collection then individual shape geometry
    const shapes = slide.shapes;
    shapes.load("items");
    await context.sync();

    shapes.items.forEach((shape) => shape.load(["id", "name", "top", "left", "height", "width", "type"]));
    await context.sync();


    // Do not preload text properties for all shapes; handle per-shape with guards below

    let result: SlideShapeInfo[] = [];
    
    for (const shape of shapes.items) {
      let textContent = "";
      
      try {
        // Guarded per-shape text loading to avoid errors on non-text shapes (e.g., images)
        if (shape.textFrame) {
          shape.textFrame.load(["textRange"]);
          await context.sync();

          if (shape.textFrame.textRange) {
            shape.textFrame.textRange.load(["text"]);
            await context.sync();
            textContent = String(shape.textFrame.textRange.text || "");
          }
        }
      } catch (error) {
        logger.warn("Could not extract text from shape:", {
          shapeId: shape.id,
          error: error,
        });
      }

      logger.info("Shape processed:", {
        shapeId: shape.id,
        shapeName: shape.name,
        shapeType: shape.type,
        textContent: textContent,
        hasTextFrame: !!shape.textFrame,
      });

      if(shape.type === 'Graphic'){
        result.push({
          id: shape.id,
          slideId: slide.id,
          name: shape.name,
          top: Number(shape.top),
          left: Number(shape.left),
          height: Number(shape.height),
          width: Number(shape.width),
          text: "",
        });
      }else{
        result.push({
          id: shape.id,
          slideId: slide.id,
          name: shape.name,
          top: Number(shape.top),
          left: Number(shape.left),
          height: Number(shape.height),
          width: Number(shape.width),
          text: processTextWithNumbering(textContent),
        });
      }
    }
    const slideShapesData: any = {
      slideId: slide.id,
      width: slideWidth,
      height: slideHeight,
      shapes: result,
      targetShapeId: targetShapeId,
    };

    const targetShape = shapes.items.find((shape: any) => shape.id === targetShapeId);
    
    
    
  
      // Write Function For Add Label To Shapes
     const labeledShapes =  await addLabelToShapes(slideShapesData.shapes as SlideShapeInfo[], targetShape as any, {height: slideShapesData.height, width: slideShapesData.width});
      // Send slide shapes data to backend server
      const labeledSlideShapesData = {
        ...slideShapesData,
        shapes: labeledShapes
      };

    // Log the slide shapes data
    logger.info("getAllShapesFromPlaceholderSlide", labeledSlideShapesData);
    localStorage.setItem("slideShapesData", JSON.stringify(labeledSlideShapesData)); 
    return labeledSlideShapesData;
  });
}

// Function to send slide shapes data to backend server
export async function sendSlideShapesToServer(slideShapesData: any): Promise<{ slideDimensions: { width: number; height: number }; shapesCount: number; labeledShapes: SlideShapeInfo[] }> {
  try {
    const serverUrl = process.env.API_URL;
    const endpoint = `${serverUrl}/slide-shapes/process`;
    
    logger.info("Sending slide shapes data to server:", {
      endpoint,
      slideId: slideShapesData.slideId,
      shapesCount: slideShapesData.shapes.length
    });

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
      },
      body: JSON.stringify(slideShapesData)
    });

    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }

    const result: any = await response.json();
    logger.info("Server response received:", result);
    return { slideDimensions: result.slideDimensions, shapesCount: result.shapesCount, labeledShapes: result.labeledShapes };
    
  } catch (error) {
    logger.error("Failed to send slide shapes data to server:", error);
    return { slideDimensions: { width: 0, height: 0 }, shapesCount: 0, labeledShapes: [] };
  }
}

// Helper function to check if two shapes match in dimensions (with tolerance for floating point)
function shapesMatchDimensions(shape1: SlideShapeInfo, shape2: SlideShapeInfo, tolerance: number = 0.1): boolean {
  return (
    Math.abs(shape1.height - shape2.height) < tolerance &&
    Math.abs(shape1.width - shape2.width) < tolerance
  );
}

// Function to add labels to shapes based on slide title and subtitle detection
async function addLabelToShapes(shapes: SlideShapeInfo[], targetShape: any, slideDimensions: {height: number, width: number}): Promise<SlideShapeInfo[]> {
  try {
    logger.info("Starting shape labeling process", {
      shapesCount: shapes.length,
      slideDimensions,
      targetShapeId: targetShape?.id
    });

    // Initialize hash map to track labeled shapes - only label unlabeled shapes
    const labeledShapesMap = new Map<string, SlideShapeInfo>();

    // Step 1: Find all shapes that match target shape dimensions (dummy targets)
    const matchingTargetShapes: SlideShapeInfo[] = [];
    if (targetShape) {
      const heightTolerance = 10; // 10 point height tolerance
      const widthTolerance = 0.1; // Small tolerance for width (floating point)
      
      matchingTargetShapes.push(...shapes.filter(shape => {
        return (
          Math.abs(shape.height - targetShape.height) <= heightTolerance ||
          (Math.abs(shape.width - targetShape.width) <= widthTolerance && Math.abs(shape.height - targetShape.height) <= heightTolerance)
        );
      }));
    }

    logger.info("Found shapes matching target dimensions:", {
      targetShapeId: targetShape?.id,
      matchingShapesCount: matchingTargetShapes.length,
      matchingShapeIds: matchingTargetShapes.map(s => s.id)
    });

    // Step 2: Detect Slide Title (global detection, only once)
    const slideTitle = detectSlideTitle(shapes, slideDimensions);
    logger.info("Detected slide title:", slideTitle);

    // Step 3: Detect Slide Subtitle (global detection, only once)
    const slideSubtitle = detectSlideSubtitle(shapes, slideTitle);
    logger.info("Detected slide subtitle:", slideSubtitle);

    // Step 4: Label global shapes first (Slide Title, Slide Subtitle, Key Take-Aways)
    shapes.forEach(shape => {
      let label: string | undefined = undefined;

      if (slideTitle && shape.id === slideTitle.id) {
        label = "Slide Title";
      } else if (slideSubtitle && shape.id === slideSubtitle.id) {
        label = "Slide Subtitle";
      } else if (detectKeyTakeAways(shape, slideDimensions)) {
        label = "Key Take-Aways";
      }

      if (label) {
        labeledShapesMap.set(shape.id, {
          ...shape,
          label
        });
      }
    });

    // Step 5: Process each matching target shape one by one
    let contentCounter = 0;
    let placeholderTitleCounter = 1;
    let placeholderSubtitleCounter = 1;
    let sourceCounter = 1;
    let notesCounter = 1;
    for (const dummyTargetShape of matchingTargetShapes) {
      // Skip if this shape is already labeled (from global detection)
      if (labeledShapesMap.has(dummyTargetShape.id)) {
        continue;
      }

      // Create a dummy target object for detection functions
      const dummyTarget = {
        id: dummyTargetShape.id,
        top: dummyTargetShape.top,
        left: dummyTargetShape.left,
        height: dummyTargetShape.height,
        width: dummyTargetShape.width
      };

      // Detect Placeholder Title and Subtitle using dummy target as reference
      const placeholderDetection = await detectPlaceholderTitleAndSubtitle(dummyTarget, shapes, slideDimensions);
      logger.info(`Detected placeholder title and subtitle for dummy target ${dummyTargetShape.id}:`, placeholderDetection);

      const placeholderTitle = placeholderDetection.title;
      const placeholderSubtitle = placeholderDetection.subTitle;

      // Detect Source and Notes using dummy target as reference
      const sourceNotesDetection = await detectSourceAndNotes(dummyTarget, shapes, slideDimensions);
      logger.info(`Detected source and notes for dummy target ${dummyTargetShape.id}:`, sourceNotesDetection);

      const sourceShape = sourceNotesDetection.source;
      const notesShape = sourceNotesDetection.notes;

      // Label shapes based on this dummy target detection (only if not already labeled)
      shapes.forEach(shape => {
        // Skip if already labeled
        if (labeledShapesMap.has(shape.id)) {
          return;
        }

        let label: string | undefined = undefined;

        if (placeholderTitle && shape.id === placeholderTitle.id) {
          label = `Placeholder Title ${ dummyTarget.id === targetShape.id ? "" : placeholderTitleCounter++}`;
        } else if (placeholderSubtitle && shape.id === placeholderSubtitle.id) {
          label = `Placeholder Subtitle ${ dummyTarget.id === targetShape.id ? "" : placeholderSubtitleCounter++}`;
        } else if (sourceShape && shape.id === sourceShape.id) {
          label = `Source ${ dummyTarget.id === targetShape.id ? "" : sourceCounter++}`;
        } else if (notesShape && shape.id === notesShape.id) {
          label = `Notes ${ dummyTarget.id === targetShape.id ? "" : notesCounter++}`;
        }

        if (label) {
          labeledShapesMap.set(shape.id, {
            ...shape,
            label
          });
        }
      });
    }

    // Step 6: Label matching target shapes as "Content" with suffix
    for (const shape of matchingTargetShapes) {
      // Skip if already labeled
      if (labeledShapesMap.has(shape.id)) {
        continue;
      }

      // Original target shape gets "Target Shape" label, others get "Content" with suffix
      if (targetShape && shape.id === targetShape.id) {
        labeledShapesMap.set(shape.id, {
          ...shape,
          label: "Target Shape"
        });
      } else {
        labeledShapesMap.set(shape.id, {
          ...shape,
          label: `Content ${contentCounter++}`
        });
      }
    }

    // Step 7: Label remaining unlabeled shapes as "Unlabeled"
    shapes.forEach(shape => {
      if (!labeledShapesMap.has(shape.id)) {
        labeledShapesMap.set(shape.id, {
          ...shape,
          label: "Unlabeled"
        });
      }
    });

    // Convert map to array
    const labeledShapes = Array.from(labeledShapesMap.values());

    // Count detected elements
    const sourceShapes = labeledShapes.filter(s => s.label === "Source");
    const notesShapes = labeledShapes.filter(s => s.label === "Notes");
    const keyTakeAwaysShapes = labeledShapes.filter(s => s.label === "Key Take-Aways");
    const contentShapes = labeledShapes.filter(s => s.label?.startsWith("Content"));

    // Log the labeled shapes
    logger.info("Shape labeling completed:", {
      totalShapes: labeledShapes.length,
      slideTitle: slideTitle?.id,
      slideSubtitle: slideSubtitle?.id,
      targetShape: targetShape?.id,
      matchingTargetShapesCount: matchingTargetShapes.length,
      sourceShapes: sourceShapes.length,
      notesShapes: notesShapes.length,
      keyTakeAwaysShapes: keyTakeAwaysShapes.length,
      contentShapes: contentShapes.length,
      labels: labeledShapes.map(s => ({ id: s.id, name: s.name, label: s.label }))
    });

    // Store labeled shapes in localStorage
    return labeledShapes;

  } catch (error) {
    logger.error("Error in addLabelToShapes:", error);
    return [];
  }
}






export default getAllShapesFromPlaceholderSlide;

